import {
  Grid,
  IconButton,
  Alert,
  Box,
  Button,
  TextField,
  Chip,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import AssignmentIcon from "@mui/icons-material/Assignment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getLawsuits,
  deleteLawsuit,
  updateLawsuit,
  downloadFilePost,
  previewRevertSpreadsheet,
  confirmRevertSpreadsheet,
} from "../../services/LawsuitService";
import { getCustomStatusOptions } from "../../services/CustomStatusService";
import { useAuth } from "../../context/AuthContext";

import DataGridCustom from "../General/DatagridCustom";
import ActionHeaderCard from "../General/ActionHeaderCard";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import EditDialog from "../General/Dialog/EditDialog";
import { useToast } from "../../context/ToastContext";

export default function ManagerLawsuit() {
  const { showToast } = useToast();
  const { lawyerId } = useParams();
  const [lawsuits, setLawsuits] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { hasPermission } = useAuth();

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editData, setEditData] = useState({
    type: "",
    order_number: "",
    identification: "",
    dni_cuit: "",
    first_name: "",
    last_name: "",
    person_type: "",
    amount: "",
    decree: "",
    date: "",
    contribution: "",
    address: "",
    status_tax_id: "",
  });
  const [statusTaxOptions, setStatusTaxOptions] = useState([]);

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [openRevert, setOpenRevert] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);
  const [revertForm, setRevertForm] = useState({
    spreadsheet_number: "",
    reason: "",
  });
  const [revertPreview, setRevertPreview] = useState(null);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fetchLawsuits = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const search = filterModel?.quickFilterValues?.[0] || "";

      const res = await getLawsuits({
        page: page + 1,
        perPage: pageSize,
        search,
        ...(lawyerId ? { lawyer_id: lawyerId } : {}),
      });

      setLawsuits(res.data.data);
      setTotal(res.data.total);
    } catch (error) {
      console.error("[ManagerLawsuit] Error:", error);
      showToast("No se pudieron cargar los títulos.", "error")
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, lawyerId, showToast]);

  useEffect(() => {
    fetchLawsuits();
  }, [paginationModel, filterModel, fetchLawsuits]);

  useEffect(() => {
    getCustomStatusOptions("lawsuit_tax")
      .then((res) => setStatusTaxOptions(res.data?.data || []))
      .catch(() => setStatusTaxOptions([]));
  }, []);

  const handleDeleteOpen = (id) => {
    setDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const res = await deleteLawsuit(deleteId);
      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenEdit(false);
        setOpenDeleteConfirm(false);
        setDeleteId(null);
        await fetchLawsuits();
      }
    } catch {
      showToast("Error al eliminar título.", "error")
    }
  };

  const handleEditOpen = (row) => {
    setSelectedRow(row);
    setEditData({
      type: row.type || "",
      order_number: row.order_number || "",
      identification: row.identification || "",
      dni_cuit: row.dni_cuit || "",
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      person_type: row.person_type || "",
      amount: row.amount ?? "",
      decree: row.decree || "",
      date: row.date ? row.date.substring(0, 10) : "",
      contribution: row.contribution || "",
      address: row.address || "",
      status_tax_id: row.status_tax?.id ?? "",
    });
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!selectedRow) return;

    try {
      const payload = { ...editData };
      if (payload.amount === "") delete payload.amount;
      payload.status_tax_id = payload.status_tax_id === "" ? null : payload.status_tax_id;

      const res = await updateLawsuit(selectedRow.id, payload);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenEdit(false);
        await fetchLawsuits();
      }
    } catch {
      showToast("Error al actualizar título.", "error")
    }
  };

  const previewLawsuit = async (id, hasPdf) => {
    if (!hasPdf) {
      showToast(
        "Este título no tiene PDF adjunto (carga histórica u otro origen sin archivo).",
        "info",
      );
      return;
    }

    try {
      const response = await downloadFilePost(`lawsuit/${id}/download`);
      const fileURL = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPreviewUrl(fileURL);
      setOpenPreview(true);
    } catch {
      showToast("No se pudo mostrar el archivo.", "error")
    }
  };

  const handleOpenRevert = () => {
    setRevertForm({
      spreadsheet_number: "",
      reason: "",
    });
    setRevertPreview(null);
    setOpenRevert(true);
  };

  const handlePreviewRevert = async () => {
    try {
      setRevertLoading(true);
      const res = await previewRevertSpreadsheet({
        spreadsheet_number: revertForm.spreadsheet_number,
      });

      setRevertPreview(res.data?.data || null);
    } catch (error) {
      setRevertPreview(null);
      showToast(
        error?.response?.data?.message ||
          "No se pudo obtener la previsualización de la reversión.",
      "error")
    } finally {
      setRevertLoading(false);
    }
  };

  const handleConfirmRevert = async () => {
    try {
      setRevertLoading(true);
      const res = await confirmRevertSpreadsheet({
        spreadsheet_number: revertForm.spreadsheet_number,
        reason: revertForm.reason,
      });

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenRevert(false);
        setRevertPreview(null);
        setRevertForm({
          spreadsheet_number: "",
          reason: "",
        });
        await fetchLawsuits();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "No se pudo revertir la planilla.",
      "error")
    } finally {
      setRevertLoading(false);
    }
  };

  const columns = [
    { field: "code", headerName: "Codigo", flex: 1 },
    {
      field: "legalFile",
      headerName: "Expediente",
      flex: 1,
      renderCell: (params) => {
        const file = params.row.legalFile;
        const code = file?.code ?? "Sin código";

        if (!file?.id) {
          return code;
        }

        return (
            <Button
              component="a"
              href={`/legal/file/${file.id}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              sx={{
                minWidth: "auto",
                p: 0,
                fontWeight: 900,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "#7d7d7d",
              }}
            >
              {code}
            </Button>
        );
      },
    },
    { field: "juc_id", headerName: "JUC ID", flex: 1 },
    { field: "dni_cuit", headerName: "DNI/CUIT", flex: 1 },
    { field: "first_name", headerName: "Nombre/Razon Social", flex: 1 },
    {
      field: "status_label",
      headerName: "Estado",
      flex: 1,
      renderCell: (params) => {
        let chipProps = { variant: "outlined", label: params.value };

        if (params.row.status === "success") chipProps.color = "success";
        else if (params.row.status === "fail") chipProps.color = "error";
        else if (params.row.status === "attachment_failed")
          chipProps.color = "error";
        else if (params.row.status === "pending") chipProps.color = "warning";
        else if (
          params.row.status === "concluded" ||
          params.row.status === "concluded_manual"
        ) {
          chipProps.sx = {
            color: "#830cc4",
            borderColor: "#830cc4",
          };
        }

        return <Chip {...chipProps} />;
      },
    },
    {
      field: "status_tax",
      headerName: "Estado de Pago",
      flex: 1,
      valueGetter: (value) => value?.name || "—",
    },
    {
      field: "amount",
      headerName: "Monto",
      flex: 1,
      align: "right",
      valueFormatter: (value) =>
        value
          ? Number(value).toLocaleString("es-AR", {
              style: "currency",
              currency: "ARS",
            })
          : "",
    },
    {
      field: "lawyers",
      headerName: "Abogado",
      flex: 1.2,
      valueGetter: (params) => {
        if (!params) return "";
        const name = [params.name, params.lastname].filter(Boolean).join(" ");
        return params.enrollment ? `${name} (${params.enrollment})` : name;
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip
            title={
              params.row.has_pdf
                ? ""
                : "No tiene archivo registrado: se migró históricamente sin archivo adjunto."
            }
          >
            <span>
              <IconButton
                color={params.row.has_pdf ? "primary" : "default"}
                onClick={() => previewLawsuit(params.row.id, params.row.has_pdf)}
                disabled={!params.row.has_pdf}
              >
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>

          <IconButton
            color="warning"
            onClick={() => handleEditOpen(params.row)}
            disabled={!hasPermission("ver detalle demandas")}
          >
            <EditIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleDeleteOpen(params.row.id)}
            disabled={!hasPermission("eliminar demandas")}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>

        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
            <ActionHeaderCard
              icon={<AutorenewIcon />}
              title="Reversión de planillas cargadas"
              description="Revierte una planilla en estado cargado para volver a dejar sus títulos en pendiente."
              action={
                <Button
                  onClick={handleOpenRevert}
                  variant="contained"
                  // color="warning"
                  startIcon={<AutorenewIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Revertir planilla
                </Button>
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ width: "100%" }}>
          <DataGridCustom
            rows={lawsuits}
            columns={columns}
            title={lawyerId ? "Títulos del Abogado" : "Todos los Títulos"}
            description={
              lawyerId
                ? "Ver los títulos electrónicos asociados al abogado."
                : "Vista general de todos los títulos del sistema."
            }
            icon={<AssignmentIcon sx={{ fontSize: 32 }} />}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            total={total}
            loading={loading}
            filterModel={filterModel}
            onFilterChange={setFilterModel}
            filterMode="server"
            isServerPagination={true}
          />
        </Box>
      </Grid>

      <EditDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSave={handleEditSave}
        title={`Detalle de Título ${selectedRow ? `#${selectedRow.id}` : ""}`}
        subtitle="Revise la información general y edite únicamente los campos habilitados."
        values={{
          ...selectedRow,
          ...editData,
          original_name:
            selectedRow?.original_name || selectedRow?.file_name || "",
          storage_path: selectedRow?.storage_path || "",
          spreadsheet_number: selectedRow?.spreadsheet_number || "",
          status_label: selectedRow?.status_label || "",
          created_at: selectedRow?.created_at
            ? new Date(selectedRow.created_at).toLocaleString("es-AR")
            : "",
          updated_at: selectedRow?.updated_at
            ? new Date(selectedRow.updated_at).toLocaleString("es-AR")
            : "",
        }}
        setValues={setEditData}
        saveDisabled={!hasPermission("actualizar demandas")}
        sections={[
          {
            title: "Información general",
            description: "Datos solo de lectura.",
            icon: <DescriptionOutlinedIcon />,
            fields: [
              {
                name: "original_name",
                label: "Archivo original",
                readOnly: true,
              },
              {
                name: "storage_path",
                label: "Ruta en storage",
                readOnly: true,
              },
              {
                name: "spreadsheet_number",
                label: "Número de planilla SAC",
                readOnly: true,
              },
              { name: "status_label", label: "Estado", readOnly: true },
              { name: "created_at", label: "Creado", readOnly: true },
              { name: "updated_at", label: "Actualizado", readOnly: true },
            ],
          },
          {
            title: "Datos editables",
            description: "Campos que pueden modificarse.",
            icon: <EditOutlinedIcon />,
            fields: [
              { name: "order_number", label: "N° Orden" },
              { name: "identification", label: "Identificación" },
              { name: "dni_cuit", label: "DNI/CUIT" },
              { name: "first_name", label: "Nombre" },
              { name: "last_name", label: "Apellido" },
              {
                name: "person_type",
                label: "Tipo Persona",
                type: "select",
                options: [
                  { value: "F", label: "Física" },
                  { value: "J", label: "Jurídica" },
                ],
              },
              { name: "amount", label: "Monto", type: "number" },
              { name: "decree", label: "Decreto" },
              { name: "date", label: "Fecha", type: "date" },
              { name: "contribution", label: "Contribución" },
              {
                name: "address",
                label: "Dirección",
                multiline: true,
                minRows: 2,
                md: 12,
              },
              {
                name: "status_tax_id",
                label: "Estado del Sistema",
                type: "select",
                options: [
                  { value: "", label: "Sin estado" },
                  ...statusTaxOptions.map((option) => ({
                    value: option.id,
                    label: option.code ? `${option.code} - ${option.name}` : option.name,
                  })),
                ],
                md: 12,
              },
            ],
          },
        ]}
      />

      <AppDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        title="Vista previa del archivo"
        subtitle="Visualización del PDF asociado."
        icon={<VisibilityIcon />}
        maxWidth="lg"
        fullScreenMobile
        stickyHeader
        contentSx={{ p: 0, background: "transparent" }}
        paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
        // actions={
        //   <Button
        //     onClick={() => setOpenPreview(false)}
        //     variant="contained"
        //     sx={{ borderRadius: 2, fontWeight: 900 }}
        //   >
        //     Cerrar
        //   </Button>
        // }
      >
        {previewUrl ? (
          <Box
            sx={{
              p: { xs: 1, sm: 1.5 },
              background: "rgba(255,255,255,0.45)",
            }}
          >
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(255,255,255,0.72)",
              }}
            >
              <iframe
                src={previewUrl}
                style={{
                  width: "100%",
                  height: isMobile ? "calc(100vh - 110px)" : "80vh",
                  border: 0,
                }}
                title="preview"
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              py: 8,
              px: 2,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
              No se pudo cargar el archivo
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Intente nuevamente más tarde.
            </Typography>
          </Box>
        )}
      </AppDialog>

      <AppDialog
        open={openRevert}
        onClose={() => setOpenRevert(false)}
        title="Revertir planilla cargada"
        subtitle="Revise la planilla antes de confirmar la reversión."
        icon={<AutorenewIcon />}
        maxWidth="md"
        fullScreenMobile
        stickyHeader
        contentSx={{
          p: { xs: 1.2, sm: 2 },
          background: "transparent",
        }}
        paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
        actions={
          <>
            {/* <Button
              onClick={() => setOpenRevert(false)}
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              Cancelar
            </Button> */}

            <Button
              onClick={handleConfirmRevert}
              variant="contained"
              color="warning"
              disabled={
                revertLoading ||
                !revertPreview?.can_revert ||
                !revertForm.reason?.trim()
              }
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                textTransform: "none",
                px: 3,
              }}
            >
              {revertLoading ? "Procesando..." : "Confirmar reversión"}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 7 }}>
            <TextField
              label="Número de planilla SAC"
              value={revertForm.spreadsheet_number}
              onChange={(e) =>
                setRevertForm({
                  ...revertForm,
                  spreadsheet_number: e.target.value,
                })
              }
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Button
              variant="outlined"
              onClick={handlePreviewRevert}
              disabled={!revertForm.spreadsheet_number || revertLoading}
              fullWidth
              sx={{
                height: "100%",
                minHeight: 56,
                borderRadius: 2.5,
                fontWeight: 900,
                textTransform: "none",
              }}
            >
              {revertLoading ? "Buscando..." : "Buscar planilla"}
            </Button>
          </Grid>

          {revertPreview && (
            <>
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity={revertPreview.can_revert ? "warning" : "error"}
                  sx={{
                    mb: 0.5,
                    borderRadius: 3,
                    "& .MuiAlert-message": {
                      fontWeight: 700,
                    },
                  }}
                >
                  {revertPreview.can_revert
                    ? "La planilla puede revertirse. Esta acción limpiará el número y código de planilla, y dejará los títulos en pendiente."
                    : "La planilla no puede revertirse."}
                </Alert>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "rgba(255,255,255,0.62)",
                    p: { xs: 1.2, sm: 1.5 },
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Planilla"
                        value={revertPreview.spreadsheet_number || ""}
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Código de presentación"
                        value={revertPreview.spreadsheet_code || ""}
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Abogado"
                        value={
                          revertPreview.lawyer
                            ? `${revertPreview.lawyer.name}${
                                revertPreview.lawyer.enrollment
                                  ? ` (${revertPreview.lawyer.enrollment})`
                                  : ""
                              }`
                            : ""
                        }
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Cantidad de títulos"
                        value={revertPreview.total_lawsuits || 0}
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Códigos de títulos afectados"
                        value={(revertPreview.lawsuits || [])
                          .map((item) => item.code)
                          .join(", ")}
                        fullWidth
                        multiline
                        minRows={3}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {!revertPreview.can_revert &&
                (revertPreview.block_reasons || []).map((reason, index) => (
                  <Grid key={index} size={{ xs: 12 }}>
                    <Alert severity="error" sx={{ borderRadius: 3 }}>
                      {reason}
                    </Alert>
                  </Grid>
                ))}

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Motivo de reversión"
                  value={revertForm.reason}
                  onChange={(e) =>
                    setRevertForm({
                      ...revertForm,
                      reason: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Describa brevemente el motivo de la reversión..."
                />
              </Grid>
            </>
          )}
        </Grid>
      </AppDialog>

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => {
          setOpenDeleteConfirm(false);
          setDeleteId(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Confirmar eliminación"
        description="¿Estás seguro de que quieres eliminar este título? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />
    </Grid>
  );
}
